import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { useDisclosure, DisclosureModal, DisclosurePersistentBanner } from './DisclosureBanner';
import { BiometricGate, useBiometricGate } from './BiometricGate';
import { SignalRating, useBriefingRatings } from './components/SignalRating';
import { PEWidget } from './components/PEWidget';
import { AudioPlayer } from './components/AudioPlayer';
import ActionRail from './components/ActionRail';
import HeroArt from './components/HeroArt';
import audioBus from './components/audioBus';
import { EngraveFilterDefs } from './components/EngravingImage';
import './components/editorial.css';
import SubscriberDashboard from './SubscriberDashboard';
import FeedbackButton from './components/FeedbackButton';
import { useAnalytics } from './hooks/useAnalytics';
import { SourceListInline } from './components/SourceList';
import { useDemographics, DemographicsModal } from './DemographicsModal';
import './App.css';
import './uiux-polish.css';
import './broadsheet-texture.css'; // loaded last: additive broadsheet texture/atmosphere overrides
import InvitationPage from './components/InvitationPage';
import AdminBar from './AdminBar';
import { GeographicLandingPage } from './components/GeographicLandingPage';
import { PasswordResetPage } from './components/PasswordResetPage';
import PublishedBriefingView from './PublishedBriefingView';
import FrontPage from './FrontPage';
import SundayEdition from './SundayEdition';
import MastheadNameplate from './components/MastheadNameplate';
import MastheadNameplateStacked from './components/MastheadNameplateStacked';
import PaywallGate from './components/PaywallGate';

// Page One (1950s front-page redesign) — opt-in, never disturbs the live Brief
// unless deliberately enabled. Three ways to turn it on:
//   • build-time:  REACT_APP_PAGE_ONE=false  (opt OUT; default is ON)
//   • runtime URL: ?page1=0  (force off)  — no rebuild needed
//   • sticky:      localStorage 'bi_page_one_off' = '1'
// The URL param also persists itself to localStorage so it survives navigation.
const PAGE_ONE = (() => {
  // Page One is the default front page. Opt OUT with REACT_APP_PAGE_ONE=false
  // at build time, or ?page1=0 at runtime (sticky via 'bi_page_one_off').
  // NOTE: the old opt-IN key 'bi_page_one' is deliberately no longer read —
  // it made desktop (which had visited ?page1=1) diverge from mobile.
  if (process.env.REACT_APP_PAGE_ONE === 'false') return false;
  if (typeof window === 'undefined') return true;
  try {
    const q = new URLSearchParams(window.location.search).get('page1');
    if (q === '1') { localStorage.removeItem('bi_page_one_off'); return true; }
    if (q === '0') { localStorage.setItem('bi_page_one_off', '1'); return false; }
    return localStorage.getItem('bi_page_one_off') !== '1';
  } catch (_) { return true; }
})();

// Route-level views that pull heavy deps (mapbox-gl, d3, the admin editor tree)
// are code-split so they stay out of the subscriber's initial bundle.
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const GlobalView = lazy(() => import('./components/GlobalInterface/GlobalView'));
const LogisticsTab = lazy(() => import('./LogisticsTab'));

// Magnetic-declination mark (true-north / magnetic-north fork), reused to flank
// the masthead tagline. Same SVG as the .vol-line::after decoration.
const DeclinationSymbol = ({ mirrored = false }) => (
  <svg width="24" height="14" viewBox="0 0 34 20" aria-hidden="true"
    style={{ flexShrink: 0, transform: mirrored ? 'scaleX(-1)' : undefined }}>
    <g fill="none" stroke="rgba(13,13,13,0.35)"><path d="M14 18V3M14 18 8 4" /></g>
    <path d="m14 3-1.6 3h3.2zM8 4l.2 3.2 2.4-1.8z" fill="rgba(13,13,13,0.35)" />
    <text x="16" y="6" fontFamily="monospace" fontSize="5" fill="rgba(13,13,13,0.42)">TN</text>
    <text y="6" fontFamily="monospace" fontSize="5" fill="rgba(13,13,13,0.42)">MN</text>
  </svg>
);

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const AuthPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const endpoint = isSignup ? `${API_URL}/api/auth/signup` : `${API_URL}/api/auth/login`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const text = await response.text();
      const data = JSON.parse(text);
      if (response.ok) {
        localStorage.setItem('token', data.token);
        onLogin(data.token);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (response.ok) {
        setResetMessage('Password reset link sent to your email. Check your inbox.');
        setEmail('');
      } else {
        setError(data.error || 'Failed to send reset link');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 style={{ fontFamily: "var(--font-display)" }}>Bearing Intelligence</h1>
        <p className="auth-tagline">Know your bearing.</p>

        {isForgotPassword ? (
          <form onSubmit={handleForgotPassword}>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit">Send Reset Link</button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="submit">{isSignup ? 'Create Account' : 'Sign In'}</button>
          </form>
        )}

        <p className="auth-toggle">
          {isForgotPassword ? (
            <>
              Remember your password?
              <button type="button" onClick={() => { setIsForgotPassword(false); setError(''); setResetMessage(''); }} className="link-button">
                Sign in
              </button>
            </>
          ) : isSignup ? (
            <>
              Have an account?
              <button type="button" onClick={() => setIsSignup(!isSignup)} className="link-button">
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?
              <button type="button" onClick={() => setIsSignup(!isSignup)} className="link-button">
                Sign up
              </button>
            </>
          )}
        </p>

        {!isForgotPassword && !isSignup && (
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => { setIsForgotPassword(true); setError(''); setPassword(''); }}
              className="link-button"
              style={{ fontSize: '0.9rem' }}
            >
              Forgot password?
            </button>
          </p>
        )}

        {error && <p className="error">{error}</p>}
        {resetMessage && <p className="success" style={{ color: '#4ade80' }}>{resetMessage}</p>}
      </div>
    </div>
  );
};

const BriefingApp = ({ token, onLogout, onAdminStatus, viewAsSubscriber }) => {
  const { track } = useAnalytics();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBanner(false);
    setInstallPrompt(null);
  };
  const { accepted, accept } = useDisclosure();
  const { submitted, markSubmitted } = useDemographics();
  const [briefing, setBriefing] = useState(null);
  const [sundayEdition, setSundayEdition] = useState(null);
  const [todayEvents, setTodayEvents] = useState([]);
  const [hypotheses, setHypotheses] = useState([]);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today');

  // Masthead account menu (compass-dial dropdown)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);
  const goTo = (v) => { setView(v); setMenuOpen(false); };

  // Periscope masthead: a slim sticky bar drops in once the reader scrolls past the
  // headline/threat block. Prefer an IntersectionObserver on that actual block for
  // pixel-perfect timing; fall back to a scroll threshold on views that have no
  // headline block. Re-attaches when the view or briefing changes.
  const [condensed, setCondensed] = useState(false);
  const briefingKey = briefing && briefing.id;
  useEffect(() => {
    const target = document.querySelector('.briefing-header');
    if (target && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        ([entry]) => {
          // Engage once the headline/threat block has scrolled fully above the viewport.
          setCondensed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      );
      io.observe(target);
      return () => io.disconnect();
    }
    // Fallback: scroll threshold with hysteresis (non-briefing views / no IO support).
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY || window.pageYOffset || 0;
        setCondensed((prev) => (prev ? y > 200 : y > 300));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [view, briefingKey]);

  const [error, setError] = useState('');
  const [userIsPremium, setUserIsPremium] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [userEmail, setUserEmail] = useState('');

  const checkSubscription = useCallback(async (currentToken) => {
    try {
      const response = await fetch(API_URL + '/api/user/status', {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserIsPremium(data.isPremium);
        setIsAdmin(data.isAdmin || false);
        setUserEmail(data.email || '');
        onAdminStatus(data.isAdmin || false);
      } else {
        setUserIsPremium(false);
        setIsAdmin(false);
        onAdminStatus(false);
      }
    } catch (err) {
      setUserIsPremium(false);
      setIsAdmin(false);
      onAdminStatus(false);
    } finally {
      setCheckingSubscription(false);
    }
  }, [onAdminStatus]);

  const registerNotifications = useCallback(async (currentToken) => {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
      });
      await fetch(API_URL + '/api/notifications/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` },
        body: JSON.stringify({ subscription: sub })
      });
    } catch (err) {
      console.error('Notifications failed:', err);
    }
  }, []);

  const fetchBriefing = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL + '/api/briefings/today', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBriefing(await response.json());
      } else if (response.status === 404) {
        setError('Briefing not available yet. Check back at 0100 GMT.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSundayEdition = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/sunday-editions/latest');
      if (response.ok) {
        const data = await response.json();
        setSundayEdition(data.edition);
      }
    } catch (err) {}
  }, []);

  const fetchHypotheses = useCallback(async () => {
    try {
      if (!briefing?.id) return;
      const response = await fetch(`${API_URL}/api/hypotheses/${briefing?.id}`);
      if (response.ok) {
        const data = await response.json();
        setHypotheses(data.hypotheses || []);
      }
    } catch (err) {}
  }, [briefing?.id]);

  const fetchTodayEvents = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/v2/global/events?days=1', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTodayEvents(data.events || []);
      }
    } catch (err) {}
  }, [token]);

  const fetchArchive = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/briefings/archive', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setArchive(await response.json());
      }
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  // Start the real $15 Member checkout: create a Stripe Checkout Session and
  // redirect to Stripe's hosted page. (Replaces the legacy $199 inline modal.)
  const requestCheckout = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';
      const res = await fetch(API_URL + '/api/subscription/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier: 'business' }), // single Member tier ($15/mo)
      });
      if (!res.ok) throw new Error('checkout unavailable');
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error('no checkout url');
    } catch (e) {
      console.error('Checkout error:', e.message);
      window.alert('Sorry — we could not start checkout just now. Please try again in a moment.');
    }
  }, [token]);

  // Paywall CTA: "Subscribe" opens checkout when signed in, else asks App() to log in first.
  useEffect(() => {
    const onSubscribe = () => {
      if (token) requestCheckout();
      else window.dispatchEvent(new CustomEvent('bi:needlogin'));
    };
    window.addEventListener('bi:subscribe', onSubscribe);
    return () => window.removeEventListener('bi:subscribe', onSubscribe);
  }, [token, requestCheckout]);


  useEffect(() => {
    if (token) {
      checkSubscription(token);
      registerNotifications(token);
    } else {
      // Public / logged-out: no subscription to check — render the Brief preview.
      setCheckingSubscription(false);
      setUserIsPremium(false);
    }
  }, [token, checkSubscription, registerNotifications]);

  useEffect(() => {
    if (checkingSubscription) return;
    // The Brief is fetched for EVERYONE (server returns a preview payload to
    // non-Business callers); personalized/gated data only when entitled.
    fetchBriefing();
    fetchSundayEdition();
    if (userIsPremium || isAdmin) {
      fetchTodayEvents();
      fetchHypotheses();
    }
  }, [checkingSubscription, userIsPremium, isAdmin, fetchBriefing, fetchSundayEdition, fetchTodayEvents, fetchHypotheses]);

  // Poll for briefing updates every 5 minutes — picks up audio, new publications
  useEffect(() => {
    if (!userIsPremium && !isAdmin) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/briefings/today`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!res.ok) return;
        const data = await res.json();
        // Only update if something changed (audio added, status changed)
        setBriefing(prev => {
          if (!prev || !data.briefing) return prev;
          if (prev.audio_url !== data.briefing.audio_url ||
              prev.status !== data.briefing.status) {
            return data.briefing;
          }
          return prev;
        });
      } catch(e) {}
    }, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, [userIsPremium, isAdmin, token]);

  // Fetch Weekly Review on mount — public endpoint, no auth needed
  useEffect(() => {
    fetchSundayEdition();
  }, [fetchSundayEdition]);

  // Loading
  if (checkingSubscription) {
    return (
      <div className="app-container">
        <header className="app-header">
          <div className="header-left">
            <img src="/production-logo.svg" alt="Bearing Intelligence" className="app-logo" style={{ filter: 'brightness(0)' }} />
            <div className="header-text"><h1>Bearing Intelligence</h1><p><DeclinationSymbol /> Know your bearing <DeclinationSymbol mirrored /></p></div>
          </div>
          <button className="logout-btn" onClick={onLogout}>Logout</button>
        </header>
        <FeedbackButton token={token} />

      <main className="app-main"><div className="loading">Checking access...</div></main>
      </div>
    );
  }

  // Admin: only redirect if NOT in subscriber preview mode
  if (isAdmin && !viewAsSubscriber) {
    window.location.href = '/admin';
    return null;
  }

  // NOTE: the old full-page hard paywall (non-premium -> $199 wall) is removed.
  // Non-Business users now see the Brief tab as a metered public preview with an
  // inline PaywallBreak (served preview payload from the API). The checkout modal
  // is rendered in the main return and opened via requestCheckout / the paywall CTA.

  // Admin viewing as subscriber sees the EXACT subscriber experience (the tabbed view
  // below) — the only admin addition is the thin ribbon rendered by the outer App.
  // (No special PublishedBriefingView branch here: that made the preview diverge from
  // the real customer experience.)

  // Premium / standard subscriber view
  // Periscope: full masthead scrolls away; once past the headline/threat block a slim
  // sticky bar (condensed mark + briefing headline) takes over (see .bi-periscope).
  const periReduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SECTION = { today: PAGE_ONE ? 'Page 1' : 'Brief', global: 'Geography', logistics: 'Logistics', dashboard: 'Dashboard', account: 'My Account', preferences: 'My Preferences' };
  // Access tier: Page 1 + podcast are free; the depth (Page 2, articles, Geography,
  // Logistics, Sunday) is for members. Admins always see everything.
  const entitled = !!(userIsPremium || isAdmin);
  const periHeadline = (view === 'today' && briefing && briefing.daily_theme) ? briefing.daily_theme : (SECTION[view] || 'Bearing Intelligence');
  return (
    <div className={`app-container${process.env.REACT_APP_PAPER_AGING !== 'false' ? ' bi-aged' : ''}${PAGE_ONE ? ' bi-page-one' : ''}`}>

      {showInstallBanner && (
        <div style={{
          position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)',
          zIndex:490, width:'calc(100% - 32px)', maxWidth:480,
          backgroundColor:'#FFFFFF', border:'1px solid rgba(52,73,94,0.2)',
          borderRadius:8, padding:'12px 16px',
          boxShadow:'0 4px 24px rgba(0,0,0,0.1)',
          display:'flex', alignItems:'center', gap:12,
          fontFamily:"var(--font-body)"
        }}>
          <img src="/production-logo.svg" alt="" style={{ width:32, height:32, borderRadius:6, filter: 'brightness(0)' }} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'#2C3E50', fontSize:13, fontWeight:600, marginBottom:2 }}>Add to Home Screen</div>
            <div style={{ color:'#555555', fontSize:11, lineHeight:1.4 }}>Install Bearing Intelligence for the best experience</div>
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            <button onClick={() => setShowInstallBanner(false)} style={{ background:'none', border:'1px solid #D0D8E8', borderRadius:4, color:'#555555', fontSize:11, padding:'5px 8px', cursor:'pointer', fontFamily:'inherit' }}>Later</button>
            <button onClick={handleInstall} style={{ backgroundColor:'#E74C3C', border:'none', borderRadius:4, color:'#FFFFFF', fontSize:11, fontWeight:600, padding:'5px 10px', cursor:'pointer', fontFamily:'inherit' }}>Install</button>
          </div>
        </div>
      )}
      {!accepted && <DisclosureModal onAccept={accept} />}
      {accepted && !submitted && <DemographicsModal onComplete={markSubmitted} />}
      {/* BIOMETRICS DISABLED — enable for beta */}
      {false && accepted && submitted && window.__biometricAvailable && localStorage.getItem('bi_biometric_enrolled') !== 'true' && (
        <BiometricGate onVerified={() => {}} onSkip={() => {}} />
      )}
      <DisclosurePersistentBanner />
      <EngraveFilterDefs />
      {/* Periscope — slim sticky bar: condensed mark + briefing headline, black + gold rule. */}
      <div className={`bi-periscope${condensed ? ' bi-periscope--shown' : ''}`} aria-hidden={!condensed}
        style={{ pointerEvents: condensed ? 'auto' : 'none' }}>
        <span className="bi-periscope__mark" aria-hidden="true">
          <svg viewBox="0 0 30 30">
            <circle cx="15" cy="15" r="13" strokeWidth="1.5" />
            <polygon points="15,5 13,15 15,13 17,15" fill="currentColor" stroke="none" />
            <polygon points="15,25 13,15 15,17 17,15" strokeWidth="1" />
          </svg>
        </span>
        <button className="bi-periscope__headline" title="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: periReduce ? 'auto' : 'smooth' })}>
          {periHeadline}
        </button>
      </div>
      {(() => {
        // The single account/settings control — the one place a reader manages
        // their account. Rendered top-right of the masthead (Page One) or in the
        // right column (classic header). Out of the layout flow in Page One so it
        // never affects the nameplate's scaling.
        const accountMenu = (
          <div className="header-menu" ref={menuRef}>
            <button className="header-menu-btn" onClick={() => setMenuOpen(o => !o)}
              aria-label="Open account menu" aria-haspopup="true" aria-expanded={menuOpen}>
              {PAGE_ONE ? (
                /* Lopsided atomic starburst (faded blue) — mid-century motif. */
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#5E7E92" d="M15.00 1.00 L16.57 11.11L20.70 9.10 L18.74 13.09L28.07 13.16 L19.09 15.94L20.80 18.91 L17.23 18.56L16.98 29.06 L13.98 19.08L9.83 22.12 L11.40 17.16L2.72 16.73 L10.92 13.98L8.85 10.53 L13.09 11.26 Z" />
                </svg>
              ) : (
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="15" cy="15" r="13.5" stroke="currentColor" strokeWidth="1.25" />
                  <line x1="15" y1="3" x2="15" y2="7" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="15" y1="23" x2="15" y2="27" stroke="currentColor" strokeWidth="1" />
                  <line x1="3" y1="15" x2="7" y2="15" stroke="currentColor" strokeWidth="1" />
                  <line x1="23" y1="15" x2="27" y2="15" stroke="currentColor" strokeWidth="1" />
                  <line x1="5.5" y1="5.5" x2="7.6" y2="7.6" stroke="currentColor" strokeWidth="0.75" />
                  <line x1="22.4" y1="5.5" x2="24.5" y2="7.6" stroke="currentColor" strokeWidth="0.75" />
                  <line x1="5.5" y1="24.5" x2="7.6" y2="22.4" stroke="currentColor" strokeWidth="0.75" />
                  <line x1="22.4" y1="24.5" x2="24.5" y2="22.4" stroke="currentColor" strokeWidth="0.75" />
                  <polygon points="15,6 13.5,15 15,13 16.5,15" fill="currentColor" />
                  <polygon points="15,24 13.5,15 15,17 16.5,15" fill="none" stroke="currentColor" strokeWidth="0.75" />
                </svg>
              )}
            </button>
            {menuOpen && (
              <nav className="header-menu-dropdown" role="menu">
                <button className="menu-item" role="menuitem" onClick={() => goTo('account')}>My Account</button>
                <button className="menu-item" role="menuitem" onClick={() => goTo('preferences')}>My Preferences</button>
                <button className="menu-item" role="menuitem" onClick={() => goTo('dashboard')}>My Dashboard</button>
                <div className="menu-divider" role="separator" />
                <button className="menu-item menu-item--logout" role="menuitem" onClick={() => { setMenuOpen(false); onLogout(); }}>Log Out</button>
              </nav>
            )}
          </div>
        );
        return PAGE_ONE ? (
          <header className="app-header p1-masthead">
            <div className="p1-mast-account">{accountMenu}</div>
            <MastheadNameplate className="p1-nameplate p1-nameplate--wide" />
            <MastheadNameplateStacked className="p1-nameplate p1-nameplate--stacked" />
            {/* Double rule broken around the centered compass roundel, flowing to both edges. */}
            <div className="p1-mast-rule" aria-hidden="true">
              <span className="p1-mast-line" />
              <img src="/production-logo.svg" alt="" className="p1-mast-roundel" style={{ filter: 'brightness(0)' }} />
              <span className="p1-mast-line" />
            </div>
          </header>
        ) : (
          <header className="app-header app-header--tricol">
            {/* LEFT — logo */}
            <div className="header-col header-col--left">
              <img src="/production-logo.svg" alt="Bearing Intelligence" className="app-logo" style={{ filter: 'brightness(0)' }} />
            </div>
            {/* CENTER — masthead name + tagline */}
            <div className="header-col header-col--center">
              <div className="header-text"><h1>Bearing Intelligence</h1><p><DeclinationSymbol /> Know your bearing <DeclinationSymbol mirrored /></p></div>
            </div>
            {/* RIGHT — compass-dial account menu */}
            <div className="header-col header-col--right">{accountMenu}</div>
          </header>
        );
      })()}
      <div className="header-volume-container">
        <div className="vol-line">
          {(() => {
            const today = new Date();
            const dayOfWeek = today.getDay();
            const editionType = dayOfWeek === 0 ? 'Sunday' : dayOfWeek === 6 ? 'Weekend' : 'Weekday';
            return `Vol. LXXXI · No. 172 · ${editionType} Edition · Fifty Cents`;
          })()}
        </div>
      </div>
      <nav className="app-nav">
        <button className={`nav-btn ${view === 'today' ? 'active' : ''}`} onClick={() => { setView('today'); track('briefing_opened'); }}>{PAGE_ONE ? 'Page 1' : 'Brief'}</button>
        <button className={`nav-btn ${view === 'global' ? 'active' : ''}`} onClick={() => { setView('global'); track('map_opened'); }}>Geography</button>
        <button className={`nav-btn ${view === 'logistics' ? 'active' : ''}`} onClick={() => { setView('logistics'); track('logistics_visited'); }}>Logistics</button>
        {new Date().getDay() === 0 && (
          <button className={`nav-btn ${view === 'sunday' ? 'active' : ''}`} onClick={() => { setView('sunday'); track('sunday_edition_opened'); }}>Sunday Edition</button>
        )}
      </nav>
      <FeedbackButton token={token} />

      <main className="app-main">
        {view === 'today' ? (
          <TodayView briefing={briefing} loading={loading} error={error} onRefresh={fetchBriefing} sundayEdition={sundayEdition} todayEvents={todayEvents} hypotheses={hypotheses} entitled={entitled} />
        ) : view === 'global' ? (
          entitled
            ? <Suspense fallback={<div className="loading">Loading global view…</div>}><GlobalView /></Suspense>
            : <PaywallGate eyebrow="Members only · Geography" heading="Explore the Geography desk"
                blurb="The interactive map — country dossiers, threat levels, and history — is available to members. Page 1 and the daily podcast are always free." />
        ) : view === 'logistics' ? (
          entitled
            ? <Suspense fallback={<div className="loading">Loading logistics…</div>}><LogisticsTab /></Suspense>
            : <PaywallGate eyebrow="Members only · Logistics" heading="Open the Logistics desk"
                blurb="The supply-chain map — shared-material chokepoints, vulnerability dossiers, and shortage simulation — is available to members. Page 1 and the daily podcast are always free." />
        ) : view === 'sunday' ? (
          entitled
            ? <SundayEdition edition={sundayEdition} dateStr={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} />
            : <PaywallGate eyebrow="Members only · Sunday Edition" heading="Read the Weekly Review"
                blurb="The Sunday Edition — the seven-day pattern read across every domain — is available to members. Page 1 and the daily podcast are always free." />
        ) : view === 'dashboard' ? (
          <SubscriberDashboard archive={archive} fetchArchive={fetchArchive} />
        ) : view === 'account' || view === 'preferences' ? (
          <div className="bi-placeholder" style={{ maxWidth: 640, margin: "0 auto", padding: "48px 20px", textAlign: "center" }}>
            <h2 style={{ fontFamily: "var(--font-heading)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
              {view === 'account' ? 'My Account' : 'My Preferences'}
            </h2>
            <p style={{ color: "var(--color-text-light)", lineHeight: 1.7 }}>
              This section is coming soon. {view === 'account' ? 'Subscription management' : 'Display & notification preferences'} will live here.
            </p>
            <button className="nav-btn" style={{ marginTop: 20, minWidth: 160, border: "1px solid var(--color-charcoal)", padding: "10px 16px" }} onClick={() => setView('today')}>← Back to Brief</button>
          </div>
        ) : null}
      </main>
      <footer className="app-footer">
        <div className="app-footer__inner">
          <span className="app-footer__copy">© {new Date().getFullYear()} Bearing Intelligence</span>
          <nav className="app-footer__links" aria-label="Legal">
            <a href="/terms">Terms of Service</a>
            <span className="app-footer__dot" aria-hidden="true">·</span>
            <a href="/privacy">Privacy Policy</a>
          </nav>
          <span className="app-footer__tag">Find your bearing.</span>
        </div>
      </footer>
    </div>
  );
};

// Threat severity → the shared --threat-* ramp (the ONE sanctioned color axis).
// Used only as a swatch/underline accent; the deck text itself stays ink for AA.
const THREAT_RAMP = {
  CRITICAL: 'var(--threat-critical)',
  HIGH: 'var(--threat-high)',
  ELEVATED: 'var(--threat-elevated)',
  MODERATE: 'var(--threat-moderate)',
  LOW: 'var(--threat-low)',
};
const threatColor = (lvl) => THREAT_RAMP[String(lvl || '').toUpperCase().trim()] || 'var(--color-charcoal)';

const TodayView = ({ briefing, loading, error, onRefresh, entitled = false }) => {
  // Audio scrubber is hidden until the rail's Listen icon reveals it (consolidated
  // control — no standalone always-on widget). The player stays mounted so its
  // registered toggle works; only its visibility changes.
  const [playerVisible, setPlayerVisible] = useState(false);
  useEffect(() => audioBus.subscribe((s) => setPlayerVisible(s.visible)), []);
  if (loading) return <div className="loading">Loading today's briefing...</div>;
  if (error) return <div className="error-container"><p>{error}</p><button onClick={onRefresh}>Try again</button></div>;
  if (!briefing) return <div>No briefing available</div>;

  // Clean interim state while today's briefing is generating/publishing — better
  // than silently showing an older (title-less) briefing via the /today fallback.
  if (briefing.preparing || !briefing.id) {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "64px 20px", textAlign: "center" }}>
        <h1 style={{ fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 12 }}>Daily Assessment</h1>
        <p style={{ color: "var(--color-text-light)", lineHeight: 1.7 }}>Today's briefing is being prepared. Check back shortly.</p>
        <button className="nav-btn" style={{ marginTop: 20, minWidth: 140, border: "1px solid var(--color-charcoal)", padding: "10px 16px" }} onClick={onRefresh}>Refresh</button>
      </div>
    );
  }

  // Page One (flagged): the 1950s front-page redesign IS the whole brief body —
  // it renders its own lead headline, hero, KDs and radar, so we bypass the old
  // briefing-header/hero/podcast scaffolding entirely to avoid a duplicate header.
  if (PAGE_ONE && briefing.id) return <FrontPage briefingId={briefing.id} masthead={briefing} entitled={entitled} />;

  // The real subscriber "Today's Briefing" is the single source-of-truth render:
  // PublishedBriefingView in subscriber mode (complete content, method concealed,
  // monochrome). The tab shell provides the masthead; keep the podcast player here.
  return (
    <div>
      <article className="briefing-article">
        <header className="briefing-header">
          {/* Front-page headline: the extracted daily theme. Falls back to the
              standing label until the generation pipeline emits briefing.dailyTheme. */}
          <h1 style={{ fontFamily: "var(--font-display)" }}>{briefing.daily_theme || "DAILY ASSESSMENT"}</h1>
          {/* Deck line: threat level as a newspaper dek. AA-safe accent — ink text
              with the --threat color carried by a swatch + underline, not the text. */}
          {briefing.threat_level && (() => {
            const c = threatColor(briefing.threat_level);
            return (
              <div className="briefing-deck" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <span aria-hidden="true" style={{ width: 12, height: 12, background: c, display: "inline-block", flexShrink: 0 }} />
                <span style={{ fontFamily: "var(--font-subheading)", fontSize: 13, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-text-primary)", borderBottom: `3px solid ${c}`, paddingBottom: 2 }}>
                  Threat Level: {String(briefing.threat_level).toUpperCase()}
                </span>
              </div>
            );
          })()}
        </header>
        {/* Hero art — full-width, public (above the paywall cutoff). */}
        <HeroArt theme={briefing.daily_theme || ''} />
        {/* Action bar — under the hero, above the date. Listen expands the scrubber
            just below it. Single canonical icon-only bar (no floating rail). */}
        <ActionRail
          shareTitle={briefing.daily_theme ? `${briefing.daily_theme} — Bearing Intelligence` : 'Bearing Intelligence'}
          saveKey={briefing.id ? `briefing-${briefing.id}` : undefined}
        />
        {briefing.audio_url && (
          <div style={{ display: playerVisible ? 'block' : 'none' }}>
            <AudioPlayer audioUrl={briefing.audio_url} title="Daily Assessment" />
          </div>
        )}
        {briefing.id
          ? <PublishedBriefingView briefingId={briefing.id} subscriberView={true} showVersion={false} showEditHistory={false} />
          : <div className="loading">Loading briefing…</div>}
      </article>
    </div>
  );
};

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [biometricVerified, setBiometricVerified] = useState(false);
  useBiometricGate(); // initializes async platform authenticator check
  const [isAdmin, setIsAdmin] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  // Logged-out users see the public Brief preview; the paywall's "Log in" / "Subscribe"
  // CTAs raise this to show the login page.
  const [showAuth, setShowAuth] = useState(false);
  useEffect(() => {
    const openAuth = () => setShowAuth(true);
    window.addEventListener('bi:login', openAuth);
    window.addEventListener('bi:needlogin', openAuth);
    return () => {
      window.removeEventListener('bi:login', openAuth);
      window.removeEventListener('bi:needlogin', openAuth);
    };
  }, []);

  // Read from sessionStorage so the value survives page reloads
  const [viewAsSubscriber, setViewAsSubscriber] = useState(
    () => sessionStorage.getItem('viewAsSubscriber') === 'true'
  );

  // Helper: always write to both state AND sessionStorage together
  const setSubscriberView = (val) => {
    sessionStorage.setItem('viewAsSubscriber', val ? 'true' : 'false');
    setViewAsSubscriber(val);
  };

  const [viewBriefingId, setViewBriefingId] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    const search = window.location.search;

    // Check for password reset token
    const resetMatch = search.match(/token=([^&]+)/);
    if (resetMatch) {
      setResetToken(resetMatch[1]);
    }

    const match = path.match(/\/invite\/(.+)/);
    if (match) setInviteToken(match[1]);

    // Check for published briefing view
    const viewMatch = path.match(/\/view\/(\d+)/);
    if (viewMatch) setViewBriefingId(viewMatch[1]);

    // Landing on /admin always exits subscriber preview
    if (path === '/admin') {
      sessionStorage.setItem('viewAsSubscriber', 'false');
      setViewAsSubscriber(false);
    }
  }, []);

  const handleLogin = (newToken) => { setToken(newToken); setInviteToken(null); setBiometricVerified(true); };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('viewAsSubscriber');
    setToken(null);
    setIsAdmin(false);
    setViewAsSubscriber(false);
  };

  if (resetToken) return <PasswordResetPage token={resetToken} />;

  if (inviteToken) return <InvitationPage token={inviteToken} onSuccess={handleLogin} />;

  // Published briefing view / public share link (no auth required). Render the
  // clean customer view (subscriberView) with a standalone masthead so shared
  // links never expose editor-only scaffolding.
  if (viewBriefingId) return (
    <div style={{ background: "var(--color-bg-secondary)" }}>
      <PublishedBriefingView briefingId={viewBriefingId} showVersion={false} subscriberView={true} standalone={true} />
    </div>
  );

  // Biometric gate — show when token exists but biometric not yet verified this session
  // BIOMETRICS DISABLED — enable for beta: check window.__biometricAvailable && enrolled
  if (false && token && !biometricVerified && window.__biometricAvailable && localStorage.getItem('bi_biometric_enrolled') === 'true') {
    return (
      <BiometricGate
        onVerified={() => setBiometricVerified(true)}
        onSkip={() => setBiometricVerified(true)}
      />
    );
  }

  // Admin dashboard view
  if (window.location.pathname === '/admin' && token) return (
    <>
      <AdminBar onToggle={() => {
        setSubscriberView(true);        // persisted to sessionStorage BEFORE reload
        window.location.href = '/';
      }} />
      <div style={{ paddingTop: 36 }}>
        <Suspense fallback={<div className="loading">Loading admin dashboard…</div>}>
          <AdminDashboard token={token} />
        </Suspense>
      </div>
    </>
  );

  // Logged-out + user asked to log in (via a paywall CTA): show the auth page.
  if (!token && showAuth) {
    return <GeographicLandingPage onLoginSuccess={(t) => { handleLogin(t); setShowAuth(false); }} />;
  }
  // Otherwise logged-out users fall through to the public Brief preview (BriefingApp
  // with token=null renders the metered preview + inline paywall).

  // Subscriber view (with admin bar if admin)
  return (
    <>
      {isAdmin && (
        <AdminBar pageOne={PAGE_ONE} onToggle={() => {
          setSubscriberView(false);     // persisted to sessionStorage BEFORE reload
          window.location.href = '/admin';
        }} />
      )}
      <div style={{ paddingTop: isAdmin ? 36 : 0 }}>
        <BriefingApp
          token={token}
          onLogout={handleLogout}
          onAdminStatus={setIsAdmin}
          viewAsSubscriber={viewAsSubscriber}
        />
      </div>
    </>
  );
}